
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Loader2, AlertCircle } from 'lucide-react';

interface GoogleSignInProps {
  onSuccess: (userId: string) => void;
}

declare global {
  interface Window {
    google: any;
  }
}

const GoogleSignIn: React.FC<GoogleSignInProps> = ({ onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if script is already loaded
    if (document.getElementById('google-client-script')) {
      initializeGoogleSignIn();
      return;
    }

    // Load Google Sign-In script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.id = 'google-client-script';
    script.async = true;
    script.defer = true;
    script.onload = () => initializeGoogleSignIn();
    script.onerror = () => {
      setError('Failed to load Google Sign-In');
      setLoading(false);
    };
    document.body.appendChild(script);

    return () => {
      // Don't remove script to allow reuse
    };
  }, []);

  const initializeGoogleSignIn = () => {
    const clientId = process.env.VITE_GOOGLE_CLIENT_ID;

    if (!clientId) {
      setError('Google Client ID not configured');
      setLoading(false);
      return;
    }

    try {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
          auto_select: false,
        });

        const btnContainer = document.getElementById('google-signin-button');
        if (btnContainer) {
            window.google.accounts.id.renderButton(
                btnContainer,
                {
                theme: 'filled_black',
                size: 'large',
                text: 'continue_with',
                shape: 'rectangular',
                logo_alignment: 'left',
                width: 300
                }
            );
        }
      }
      setLoading(false);
    } catch (err) {
      console.error('Google Sign-In initialization error:', err);
      setError('Failed to initialize Google Sign-In');
      setLoading(false);
    }
  };

  const handleCredentialResponse = async (response: any) => {
    try {
      setLoading(true);
      
      // Decode JWT token to get user info
      const payload = JSON.parse(atob(response.credential.split('.')[1]));
      
      const { authService } = await import('../services/authService');
      const userId = await authService.signInWithGoogle(payload);
      
      onSuccess(userId);
    } catch (err) {
      console.error('Sign-in error:', err);
      setError('Authentication failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 w-full flex flex-col items-center">
      {loading && (
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="animate-spin text-[#C5A059]" size={40} />
          <p className="text-stone-500 text-sm mono">Initializing secure connection...</p>
        </div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3 w-full"
        >
          <AlertCircle className="text-red-500" size={20} />
          <p className="text-red-500 text-sm">{error}</p>
        </motion.div>
      )}

      <div className={`space-y-4 w-full flex flex-col items-center ${loading ? 'hidden' : ''}`}>
        <div id="google-signin-button" className="flex justify-center w-full min-h-[40px]"></div>
        
        <div className="flex items-center gap-3 text-stone-500 text-xs">
          <Shield size={14} />
          <p>Encrypted with 256-bit security.</p>
        </div>
      </div>
    </div>
  );
};

export default GoogleSignIn;
